import React, { useState, useEffect } from 'react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import Icon from '../../../components/AppIcon';
import { Skeleton } from '../../../components/ui/Skeleton';
import { strapiService } from '../../../utils/api';

const EmployeeEngagementMetrics = () => {
  const [selectedCompany, setSelectedCompany] = useState('all');
  const [dateRange, setDateRange] = useState('30days');
  const [downloadStats, setDownloadStats] = useState({ total: 0, trend: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDownloadStats = async () => {
      try {
        // El conteo lo hace el backend (dashboard/stats → downloadedReceipts).
        // Antes se llamaba strapiService.payrollReceipts.getAll(), que no existe
        // (la clave real es `payroll`) y además habría traído todos los recibos
        // al cliente solo para contarlos.
        const response = await strapiService.dashboard.getStats();
        setDownloadStats({ total: response?.data?.downloadedReceipts ?? 0, trend: 0 });
      } catch (error) {
        console.error('Error fetching download stats:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchDownloadStats();
  }, []);

  const engagementData = [];
  const topEmployees = [];
  const activityHeatmap = [];

  const companies = [
    { value: 'all', label: 'Todas las Empresas' },
    { value: 'constructora-abc', label: 'Constructora ABC' },
    { value: 'servicios-xyz', label: 'Servicios XYZ' },
    { value: 'comercial-def', label: 'Comercial DEF' },
    { value: 'industrias-ghi', label: 'Industrias GHI' },
    { value: 'logistica-jkl', label: 'Logística JKL' },
    { value: 'tecnologia-mno', label: 'Tecnología MNO' }
  ];

  const dateRanges = [
    { value: '7days', label: 'Últimos 7 días' },
    { value: '30days', label: 'Últimos 30 días' },
    { value: '90days', label: 'Últimos 90 días' },
    { value: 'year', label: 'Este año' }
  ];

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload?.length) {
      return (
        <div className="bg-popover border border-border rounded-lg shadow-lg p-3">
          <p className="font-medium text-foreground mb-2">{label}</p>
          {payload?.map((entry, index) => (
            <p key={index} className="text-sm" style={{ color: entry?.color }}>
              {entry?.name}: {entry?.value?.toLocaleString('es-MX')}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  const getActivityColor = (value) => {
    if (value === 0) return 'bg-muted';
    if (value < 50) return 'bg-primary/20';
    if (value < 100) return 'bg-primary/40';
    if (value < 150) return 'bg-primary/60';
    if (value < 200) return 'bg-primary/80';
    return 'bg-primary';
  };

  return (
    <div className="space-y-6">
      {/* Controls */}
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        <h2 className="text-xl font-semibold">Métricas de Participación de Empleados</h2>
        <div className="flex flex-col sm:flex-row gap-3">
          <select
            value={selectedCompany}
            onChange={(e) => setSelectedCompany(e?.target?.value)}
            className="bg-background border border-border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
          >
            {companies?.map((company) => (
              <option key={company?.value} value={company?.value}>
                {company?.label}
              </option>
            ))}
          </select>
          <select
            value={dateRange}
            onChange={(e) => setDateRange(e?.target?.value)}
            className="bg-background border border-border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
          >
            {dateRanges?.map((range) => (
              <option key={range?.value} value={range?.value}>
                {range?.label}
              </option>
            ))}
          </select>
        </div>
      </div>
      {/* Key Metrics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-card rounded-lg border p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Accesos Totales</p>
              <p className="text-2xl font-bold text-muted-foreground">N/A</p>
              <p className="text-xs text-muted-foreground mt-1">
                Sin tracking de sesiones
              </p>
            </div>
            <Icon name="LogIn" size={24} className="text-muted-foreground" />
          </div>
        </div>

        <div className="bg-card rounded-lg border p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Descargas</p>
              {loading ? (
                <Skeleton className="h-8 w-20 mt-1" />
              ) : (
                <p className="text-2xl font-bold text-foreground">{downloadStats.total.toLocaleString('es-MX')}</p>
              )}
              <p className="text-xs text-muted-foreground mt-1">
                Recibos descargados
              </p>
            </div>
            <Icon name="Download" size={24} className="text-primary" />
          </div>
        </div>

        <div className="bg-card rounded-lg border p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Sesión Promedio</p>
              <p className="text-2xl font-bold text-muted-foreground">N/A</p>
              <p className="text-xs text-muted-foreground mt-1">
                Sin tracking de sesiones
              </p>
            </div>
            <Icon name="Clock" size={24} className="text-muted-foreground" />
          </div>
        </div>

        <div className="bg-card rounded-lg border p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Satisfacción</p>
              <p className="text-2xl font-bold text-muted-foreground">N/A</p>
              <p className="text-xs text-muted-foreground mt-1">
                Sin sistema de ratings
              </p>
            </div>
            <Icon name="Star" size={24} className="text-muted-foreground" />
          </div>
        </div>
      </div>
      {/* Engagement Trends Chart */}
      <div className="bg-card rounded-lg border p-6">
        <h3 className="text-lg font-semibold mb-4">Tendencias de Participación</h3>
        <div className="w-full h-80 flex items-center justify-center">
          <div className="text-center text-muted-foreground">
            <Icon name="TrendingUp" size={48} className="mx-auto mb-4 opacity-50" />
            <p className="text-lg font-medium">Sin datos de tendencias</p>
            <p className="text-sm">No hay tracking de sesiones de usuario</p>
          </div>
        </div>
      </div>
      {/* Top Employees and Activity Heatmap */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Active Employees */}
        <div className="bg-card rounded-lg border p-6">
          <h3 className="text-lg font-semibold mb-4">Empleados Más Activos</h3>
          <div className="h-64 flex items-center justify-center">
            <div className="text-center text-muted-foreground">
              <Icon name="Users" size={48} className="mx-auto mb-4 opacity-50" />
              <p className="text-lg font-medium">Sin datos de actividad</p>
              <p className="text-sm">No hay tracking de sesiones</p>
            </div>
          </div>
        </div>

        {/* Activity Heatmap */}
        <div className="bg-card rounded-lg border p-6">
          <h3 className="text-lg font-semibold mb-4">Mapa de Calor de Actividad</h3>
          <div className="h-64 flex items-center justify-center">
            <div className="text-center text-muted-foreground">
              <Icon name="Activity" size={48} className="mx-auto mb-4 opacity-50" />
              <p className="text-lg font-medium">Sin datos de actividad</p>
              <p className="text-sm">No hay tracking de sesiones por hora</p>
            </div>
          </div>
          <div className="flex items-center justify-between mt-4 text-xs text-muted-foreground opacity-50">
            <span>Menos</span>
            <div className="flex items-center space-x-1">
              <div className="w-3 h-3 bg-muted rounded-sm"></div>
              <div className="w-3 h-3 bg-primary/20 rounded-sm"></div>
              <div className="w-3 h-3 bg-primary/40 rounded-sm"></div>
              <div className="w-3 h-3 bg-primary/60 rounded-sm"></div>
              <div className="w-3 h-3 bg-primary/80 rounded-sm"></div>
              <div className="w-3 h-3 bg-primary rounded-sm"></div>
            </div>
            <span>Más</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmployeeEngagementMetrics;